import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faUser, faChevronDown, faChevronRight, faUsers, faDownload } from '@fortawesome/free-solid-svg-icons';
import groupService from '../../api/groupService';

const AttendeeModal = ({ isOpen, onClose, groupId, eventId, event, isClass = false }) => {
  const navigate = useNavigate();
  const [rsvpGroups, setRsvpGroups] = useState([]);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  useEffect(() => {
    if (isOpen && groupId && eventId) {
      fetchAttendees();
    }
  }, [isOpen, groupId, eventId]);

  const fetchAttendees = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch RSVPs and ticket purchases in parallel
      const [rsvpResponse, ticketPurchasesResponse] = await Promise.all([
        groupService.getEventRsvps(groupId, eventId),
        groupService.getEventTicketPurchases(groupId, eventId).catch(() => ({ data: [] })) // Gracefully handle if endpoint fails
      ]);
      
      if (rsvpResponse && rsvpResponse.data && Array.isArray(rsvpResponse.data)) {
        const goingRsvps = rsvpResponse.data.filter(rsvp => rsvp.status === 'going');
        const ticketPurchases = ticketPurchasesResponse?.data || [];
        const rsvpGroupList = processAttendees(goingRsvps, ticketPurchases);
        setRsvpGroups(rsvpGroupList);
      }
    } catch (err) {
      console.error('Error fetching attendees:', err);
      setError('Failed to load attendees');
    } finally {
      setLoading(false);
    }
  };

  const processAttendees = (rsvps, ticketPurchases = []) => {
    const rsvpGroups = [];
    
    try {
      rsvps.forEach((rsvp, rsvpIndex) => {
        console.log(`Processing RSVP ${rsvpIndex}:`, rsvp);
        
        // Link ticket purchases to this RSVP via ticket_purchase_ids array
        console.log('RSVP ticket_purchase_ids:', rsvp.ticket_purchase_ids);
        console.log('All ticketPurchases:', ticketPurchases.map(p => ({ id: p.id, status: p.payment_status })));
        
        const linkedTicketPurchases = [];
        if (Array.isArray(rsvp.ticket_purchase_ids) && rsvp.ticket_purchase_ids.length > 0) {
          rsvp.ticket_purchase_ids.forEach(purchaseId => {
            const purchase = ticketPurchases.find(p => p.id === purchaseId);
            if (purchase) {
              linkedTicketPurchases.push(purchase);
            } else {
              console.warn('⚠️ Purchase ID not found in API response:', purchaseId);
            }
          });
        }
        
        console.log('Linked purchases for this RSVP:', linkedTicketPurchases.length);
        
        // Fallback: also check legacy ticket_purchase_id field
        if (rsvp.ticket_purchase_id) {
          const legacyPurchase = ticketPurchases.find(p => p.id === rsvp.ticket_purchase_id);
          if (legacyPurchase && !linkedTicketPurchases.find(p => p.id === legacyPurchase.id)) {
            linkedTicketPurchases.push(legacyPurchase);
          }
        }
        
        // Calculate total payment amount and collect detailed payment info
        let totalPaymentCents = 0;
        let paymentStatus = null;
        const paymentDetails = []; // Array of {status, amount, createdAt} for each purchase
        
        if (linkedTicketPurchases.length > 0) {
          // Sum up all ticket purchases and collect individual statuses
          linkedTicketPurchases.forEach(purchase => {
            totalPaymentCents += (purchase.total_amount_cents || 0);
            paymentDetails.push({
              status: purchase.payment_status || 'pending',
              amount: purchase.total_amount_cents || 0,
              createdAt: purchase.created_at,
              id: purchase.id
            });
          });
          
          console.log('Payment details array:', paymentDetails);
          
          // Determine overall payment status for sorting/filtering
          const statuses = linkedTicketPurchases.map(p => p.payment_status);
          if (statuses.every(s => s === 'succeeded')) {
            paymentStatus = 'succeeded';
          } else if (statuses.some(s => s === 'failed' || s === 'canceled')) {
            paymentStatus = 'failed';
          } else if (statuses.some(s => s === 'pending' || s === 'requires_payment_method')) {
            paymentStatus = 'pending';
          } else {
            paymentStatus = statuses[0] || 'unknown';
          }
        } else if (rsvp.amount_paid_cents) {
          // Fallback to RSVP's amount_paid_cents if no ticket purchases found
          totalPaymentCents = rsvp.amount_paid_cents;
          paymentStatus = 'succeeded';
          paymentDetails.push({
            status: 'succeeded',
            amount: rsvp.amount_paid_cents,
            createdAt: rsvp.created_at,
            id: null
          });
        }
        
        // Get primary attendee name
        const primaryName = `${rsvp.primary_attendee_first_name || ''} ${rsvp.primary_attendee_last_name || ''}`.trim();
        const fallbackName = rsvp.name || rsvp.email || 'Anonymous User';
        const displayName = primaryName || fallbackName;
        
        // Process attendee details from enhanced JSONB structure
        const attendeeDetails = [];
        let primaryAttendeeDetails = null;
        let hasAnyPriceInfo = false;

        // attendee_names sometimes comes back as a JSON string from the DB/client.
        // Try to normalize to an array first.
        let attendeeNamesArray = null;
        if (rsvp.attendee_names) {
          if (typeof rsvp.attendee_names === 'string') {
            try {
              attendeeNamesArray = JSON.parse(rsvp.attendee_names);
            } catch (parseErr) {
              console.warn('Could not parse attendee_names JSON string, falling back:', parseErr, rsvp.attendee_names);
            }
          } else if (Array.isArray(rsvp.attendee_names)) {
            attendeeNamesArray = rsvp.attendee_names;
          }
        }

        if (Array.isArray(attendeeNamesArray)) {
          attendeeNamesArray.forEach((attendee, attendeeIndex) => {
            try {
              let attendeeData = null;

              if (typeof attendee === 'string') {
                // Legacy format: just a string name
                attendeeData = {
                  name: attendee.trim(),
                  ticketType: 'general',
                  ticketLabel: 'General Admission',
                  pricePaidCents: null
                };
              } else if (typeof attendee === 'object' && attendee !== null) {
                const firstName = attendee.firstName || attendee.first_name || '';
                const lastName = attendee.lastName || attendee.last_name || '';
                const fullName = (attendee.name || attendee.full_name || `${firstName} ${lastName}`.trim() || '').trim();

                attendeeData = {
                  name: fullName || `${firstName} ${lastName}`.trim(),
                  ticketType: attendee.ticket_type || attendee.ticketType || 'general',
                  ticketLabel: attendee.ticket_label || attendee.ticketLabel || 'General Admission',
                  pricePaidCents: attendee.price_paid_cents ?? attendee.pricePaidCents ?? null,
                  isPrimary: attendee.is_primary || attendee.isPrimary || false
                };
              } else if (attendee !== null && attendee !== undefined) {
                attendeeData = {
                  name: String(attendee).trim(),
                  ticketType: 'general',
                  ticketLabel: 'General Admission',
                  pricePaidCents: null
                };
              }

              if (attendeeData && attendeeData.name) {
                // Record if we have explicit price info for any attendee
                if (attendeeData.pricePaidCents !== null && attendeeData.pricePaidCents !== undefined) {
                  hasAnyPriceInfo = true;
                }

                if (attendeeData.isPrimary || attendeeData.name.toLowerCase() === displayName.toLowerCase()) {
                  primaryAttendeeDetails = attendeeData;
                } else {
                  attendeeDetails.push(attendeeData);
                }
              }
            } catch (attendeeError) {
              console.error(`Error processing attendee ${attendeeIndex}:`, attendeeError, attendee);
            }
          });
        } else if (rsvp.attendee_names && typeof rsvp.attendee_names === 'string') {
          // If it's a non-JSON string (unlikely) fall back to showing it as a single legacy name
          try {
            const trimmed = rsvp.attendee_names.trim();
            if (trimmed.length > 0) {
              attendeeDetails.push({ name: trimmed, ticketType: 'general', ticketLabel: 'General Admission', pricePaidCents: null });
            }
          } catch (e) {
            console.warn('Unhandled attendee_names format:', rsvp.attendee_names);
          }
        }
        
        // Calculate total attendee count for this RSVP
        const totalAttendees = rsvp.attendee_count || (1 + attendeeDetails.length);
        
        // Format RSVP timestamp
        const rsvpDate = rsvp.created_at ? new Date(rsvp.created_at).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        }) : '';
        
        // Extract ticket pricing information from RSVP
        const isPaidTicket = rsvp.is_paid_ticket || linkedTicketPurchases.length > 0;
        const userType = rsvp.user_type || null;
        const pricingRuleApplied = rsvp.pricing_rule_applied || null;
        
        // Get primary attendee ticket info (if available from enhanced structure)
        let primaryTicketLabel = null;
        let primaryTicketPrice = null;
        
        if (primaryAttendeeDetails) {
          primaryTicketLabel = primaryAttendeeDetails.ticketLabel;
          primaryTicketPrice = primaryAttendeeDetails.pricePaidCents;
        } else if (pricingRuleApplied && pricingRuleApplied.label) {
          // Fallback to pricing_rule_applied field
          primaryTicketLabel = pricingRuleApplied.label;
          primaryTicketPrice = pricingRuleApplied.price_cents;
        } else if (userType) {
          // Fallback: convert user_type to readable label
          primaryTicketLabel = userType.split('_').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
          ).join(' ');
        }
        
        // Check if we have varied ticket types in this RSVP group
        const hasVariedTicketTypes = attendeeDetails.length > 0 && 
          attendeeDetails.some(a => a.ticketType !== (primaryAttendeeDetails?.ticketType || 'general'));
        // If ticket types are the same but we have per-attendee price info, we still want a detailed breakdown
        const showDetailedBreakdown = hasVariedTicketTypes || hasAnyPriceInfo || (primaryAttendeeDetails && primaryAttendeeDetails.pricePaidCents !== null && primaryAttendeeDetails.pricePaidCents !== undefined);
        
        rsvpGroups.push({
          id: rsvp.id,
          primaryName: displayName,
          profileImage: rsvp.profile_image || null,
          attendeeDetails, // Enhanced structure with ticket info per attendee
          primaryAttendeeDetails, // Primary attendee's ticket info
          totalAttendees,
          rsvpDate,
          hasMultipleAttendees: attendeeDetails.length > 0,
          hasVariedTicketTypes, // Flag to show ticket breakdown
          hasAnyPriceInfo,
          showDetailedBreakdown,
          // Payment information from linked ticket purchases
          isPaid: isPaidTicket,
          paymentAmount: totalPaymentCents,
          paymentStatus: paymentStatus,
          paymentDetails: paymentDetails, // Detailed array of each purchase status
          ticketPurchaseCount: linkedTicketPurchases.length,
          // Primary ticket pricing information
          ticketTypeLabel: primaryTicketLabel,
          ticketPrice: primaryTicketPrice,
          userType
        });
      });
    } catch (error) {
      console.error('Error in processAttendees:', error);
    }
    
    // Sort by primary name
    rsvpGroups.sort((a, b) => a.primaryName.localeCompare(b.primaryName));
    
    console.log('Final RSVP groups:', rsvpGroups);
    return rsvpGroups;
  };

  const getUserInitials = (name) => {
    if (!name) return 'U';
    const words = name.split(' ').filter(word => word.length > 0);
    if (words.length >= 2) {
      return `${words[0][0]}${words[1][0]}`.toUpperCase();
    }
    return name[0].toUpperCase();
  };

  const toggleRowExpansion = (rsvpId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rsvpId)) {
      newExpanded.delete(rsvpId);
    } else {
      newExpanded.add(rsvpId);
    }
    setExpandedRows(newExpanded);
  };

  // Date formatting functions (copied from EventCard for consistency)
  const formatDate = (dateString) => {
    const options = {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const formatTime = (dateString) => {
    const options = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    };
    return new Date(dateString).toLocaleTimeString(undefined, options);
  };

  // Format recurring event display
  const formatRecurringDisplay = (isRecurring, recurrencePattern, recurrenceDay, dateString) => {
    if (!isRecurring || !recurrencePattern || recurrencePattern === 'none') {
      return null; // Use normal date display
    }
    
    const time = formatTime(dateString);
    
    if (recurrencePattern === 'daily') {
      return `Every Day at ${time}`;
    } else if (recurrencePattern === 'weekly' && recurrenceDay) {
      const days = {
        'monday': 'Monday',
        'tuesday': 'Tuesday', 
        'wednesday': 'Wednesday',
        'thursday': 'Thursday',
        'friday': 'Friday',
        'saturday': 'Saturday',
        'sunday': 'Sunday'
      };
      const dayName = days[recurrenceDay.toLowerCase()] || recurrenceDay;
      return `Every ${dayName} at ${time}`;
    } else if (recurrencePattern === 'biweekly' && recurrenceDay) {
      const days = {
        'monday': 'Monday',
        'tuesday': 'Tuesday',
        'wednesday': 'Wednesday', 
        'thursday': 'Thursday',
        'friday': 'Friday',
        'saturday': 'Saturday',
        'sunday': 'Sunday'
      };
      const dayName = days[recurrenceDay.toLowerCase()] || recurrenceDay;
      return `Every Other ${dayName} at ${time}`;
    }
    
    return null; // Fallback to normal date display
  };

  // Get formatted date and time string
  const getEventDateTime = () => {
    if (!event || !event.date) return '';
    
    const recurringDisplay = formatRecurringDisplay(
      event.is_recurring, 
      event.recurrence_pattern, 
      event.recurrence_day, 
      event.date
    );
    
    return recurringDisplay || `${formatDate(event.date)}, ${formatTime(event.date)}`;
  };

  // Pagination logic
  const totalPages = Math.ceil(rsvpGroups.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageGroups = rsvpGroups.slice(startIndex, endIndex);

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      setExpandedRows(new Set()); // Reset expanded rows when changing pages
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      setExpandedRows(new Set()); // Reset expanded rows when changing pages
    }
  };

  // Reset to first page when RSVPs change
  useEffect(() => {
    setCurrentPage(1);
    setExpandedRows(new Set());
  }, [rsvpGroups]);

  // Export functionality with enhanced per-attendee ticket details
  const exportToSpreadsheet = () => {
    if (rsvpGroups.length === 0) return;

    // Check if we have any varied ticket types to determine export format
    const hasAnyVariedTickets = rsvpGroups.some(group => group.hasVariedTicketTypes);

    let csvRows;
    
    if (hasAnyVariedTickets) {
      // Detailed export with per-attendee breakdown
      const headers = ['Attendee Name', 'Type', 'Ticket Type', 'Ticket Price', 'RSVP Date', 'Payment Status', 'Total Paid'];
      csvRows = [headers.join(',')];

      rsvpGroups.forEach(group => {
        // Format payment status for export
        const formatPaymentStatus = (status) => {
          if (!status) return 'Unknown';
          switch(status) {
            case 'succeeded': return 'Paid';
            case 'pending': return 'Pending';
            case 'failed': return 'Failed';
            case 'canceled': return 'Canceled';
            case 'requires_payment_method': return 'Action Required';
            default: return status;
          }
        };
        
        // Add primary attendee row
        const primaryName = `"${group.primaryName}"`;
        const primaryType = '"Primary"';
        const primaryTicketType = group.isPaid 
          ? `"${group.primaryAttendeeDetails?.ticketLabel || group.ticketTypeLabel || 'Paid Ticket'}"`
          : '"Free RSVP"';
        const primaryTicketPrice = group.primaryAttendeeDetails?.pricePaidCents !== null
          ? `"$${(group.primaryAttendeeDetails.pricePaidCents / 100).toFixed(2)}"`
          : (group.ticketPrice !== null ? `"$${(group.ticketPrice / 100).toFixed(2)}"` : '"$0.00"');
        const rsvpDate = `"${group.rsvpDate}"`;
        const paymentStatus = group.isPaid ? `"${formatPaymentStatus(group.paymentStatus)}"` : '"Free"';
        const totalPaid = group.isPaid 
          ? `"$${(group.paymentAmount / 100).toFixed(2)}"`
          : '"$0.00"';
        
        csvRows.push([primaryName, primaryType, primaryTicketType, primaryTicketPrice, rsvpDate, paymentStatus, totalPaid].join(','));

        // Add additional attendees with their specific ticket info
        group.attendeeDetails.forEach(attendee => {
          const attendeeName = `"${attendee.name}"`;
          const attendeeType = '"Additional"';
          const attendeeTicketType = `"${attendee.ticketLabel}"`;
          const attendeeTicketPrice = attendee.pricePaidCents !== null
            ? `"$${(attendee.pricePaidCents / 100).toFixed(2)}"`
            : '"$0.00"';
          
          csvRows.push([attendeeName, attendeeType, attendeeTicketType, attendeeTicketPrice, rsvpDate, '""', '""'].join(','));
        });
      });
    } else {
      // Legacy/simplified export format
      const headers = ['Primary Attendee', 'Additional Attendees', 'Ticket Type', 'Ticket Price', 'RSVP Date', 'Payment Status', 'Total Paid'];
      csvRows = [headers.join(',')];

      // Format payment status helper (reuse from above)
      const formatPaymentStatus = (status) => {
        if (!status) return 'Unknown';
        switch(status) {
          case 'succeeded': return 'Paid';
          case 'pending': return 'Pending';
          case 'failed': return 'Failed';
          case 'canceled': return 'Canceled';
          case 'requires_payment_method': return 'Action Required';
          default: return status;
        }
      };

      rsvpGroups.forEach(group => {
        const primaryAttendee = `"${group.primaryName}"`;
        
        // Get all additional attendee names
        const additionalNames = group.attendeeDetails.map(a => a.name).join(', ');
        const additionalAttendees = additionalNames.length > 0 
          ? `"${additionalNames}"` 
          : '""';
        
        const ticketType = group.isPaid 
          ? `"${group.ticketTypeLabel || 'Paid Ticket'}"`
          : '"Free RSVP"';
        const ticketPrice = group.ticketPrice !== null 
          ? `"$${(group.ticketPrice / 100).toFixed(2)}"`
          : '"$0.00"';
        const rsvpDate = `"${group.rsvpDate}"`;
        const paymentStatus = group.isPaid ? `"${formatPaymentStatus(group.paymentStatus)}"` : '"Free"';
        const totalPaid = group.isPaid 
          ? `"$${(group.paymentAmount / 100).toFixed(2)}"`
          : '"$0.00"';
        
        csvRows.push([primaryAttendee, additionalAttendees, ticketType, ticketPrice, rsvpDate, paymentStatus, totalPaid].join(','));
      });
    }

    // Create and download file
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      const exportType = hasAnyVariedTickets ? 'Detailed' : 'Summary';
      link.setAttribute('download', `${event?.title || 'Event'}_Attendees_${exportType}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleOverlayClick = (e) => {
    // Only close if clicking directly on the overlay, not on any child elements
    if (e.target === e.currentTarget && e.target.classList.contains('attendee-modal-overlay')) {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    }
  };

  // Helper function to render payment status badge
  const renderPaymentStatusBadge = (status) => {
    switch (status) {
      case 'succeeded':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg text-sm font-semibold text-emerald-700">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Paid
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg text-sm font-semibold text-amber-700">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
            </svg>
            Pending
          </span>
        );
      case 'failed':
      case 'canceled':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg text-sm font-semibold text-red-700">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            Failed
          </span>
        );
      case 'requires_payment_method':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-lg text-sm font-semibold text-orange-700">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            Action Needed
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 border border-gray-200 rounded-lg text-sm font-semibold text-gray-600">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            Unknown
          </span>
        );
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4" onClick={handleOverlayClick}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex justify-between items-start p-6 border-b border-gray-200 bg-gradient-to-br from-gray-50 to-white">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900 mb-1">
              {event?.title || 'Event Attendees'}
              <span className="ml-3 inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-base font-semibold">
                {rsvpGroups.reduce((total, group) => total + group.totalAttendees, 0)} Total
              </span>
            </h2>
            <p className="text-sm text-gray-600">{getEventDateTime()}</p>
          </div>
          <div className="flex items-center gap-3 ml-4">
            <button 
              onClick={exportToSpreadsheet}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 shadow-sm"
            >
              <FontAwesomeIcon icon={faDownload} className="w-4 h-4" />
              Export
            </button>
            <button 
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-200"
            >
              <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
          {loading && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-gray-600">Loading attendees...</p>
            </div>
          )}
          
          {error && (
            <div className="flex items-center justify-center py-16">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
                <p className="font-medium">{error}</p>
              </div>
            </div>
          )}
          
          {!loading && !error && (
            <>
              {rsvpGroups.length > 0 ? (
                <div className="space-y-3">
                  {currentPageGroups.map((rsvpGroup) => (
                    <div key={rsvpGroup.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200">
                      {/* Main Card Content */}
                      <div 
                        className={`p-5 ${rsvpGroup.hasMultipleAttendees ? 'cursor-pointer hover:bg-gray-50' : ''} transition-colors duration-150`}
                        onClick={() => rsvpGroup.hasMultipleAttendees && toggleRowExpansion(rsvpGroup.id)}
                      >
                        <div className="flex items-start justify-between gap-4">
                          {/* Left: Attendee Info */}
                          <div className="flex items-center gap-4 flex-1 min-w-0">
                            {/* Avatar */}
                            <div className="flex-shrink-0">
                              {rsvpGroup.profileImage ? (
                                <div 
                                  className="w-12 h-12 rounded-full bg-cover bg-center border-2 border-gray-200"
                                  style={{ backgroundImage: `url(${rsvpGroup.profileImage})` }}
                                />
                              ) : (
                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center border-2 border-gray-200">
                                  <span className="text-white font-semibold text-sm">
                                    {getUserInitials(rsvpGroup.primaryName)}
                                  </span>
                                </div>
                              )}
                            </div>
                            
                            {/* Name & Guest Count */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="text-base font-semibold text-gray-900 truncate">
                                  {rsvpGroup.primaryName}
                                </h3>
                              </div>
                              <div className="flex items-center gap-3 text-sm text-gray-600">
                                <span className="flex items-center gap-1.5">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                  {rsvpGroup.rsvpDate}
                                </span>
                                {rsvpGroup.hasMultipleAttendees && (
                                  <span className="flex items-center gap-1.5 text-blue-600 font-medium">
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                      <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                                    </svg>
                                    +{rsvpGroup.attendeeDetails.length} {rsvpGroup.attendeeDetails.length === 1 ? 'Attendee' : 'Attendees'}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Right: Ticket & Payment Info */}
                          <div className="flex items-start gap-6 flex-shrink-0">
                            {/* Ticket Type */}
                            <div className="text-right">
                              <p className="text-xs font-medium text-gray-500 mb-1">Ticket Type</p>
                              {rsvpGroup.isPaid ? (
                                <>
                                  {rsvpGroup.hasMultipleAttendees ? (
                                    <div>
                                      <p className="text-sm font-semibold text-blue-600">Multiple Tickets</p>
                                      <p className="text-xs text-gray-500 mt-0.5">{rsvpGroup.totalAttendees} tickets</p>
                                    </div>
                                  ) : (
                                    <div>
                                      <p className="text-sm font-semibold text-gray-900">
                                        {rsvpGroup.primaryAttendeeDetails?.ticketLabel || rsvpGroup.ticketTypeLabel || 'General Admission'}
                                      </p>
                                      {(rsvpGroup.primaryAttendeeDetails?.pricePaidCents !== null && rsvpGroup.primaryAttendeeDetails?.pricePaidCents !== undefined) ? (
                                        <p className="text-xs text-gray-500 mt-0.5">
                                          ${(rsvpGroup.primaryAttendeeDetails.pricePaidCents / 100).toFixed(2)}
                                        </p>
                                      ) : rsvpGroup.ticketPrice !== null ? (
                                        <p className="text-xs text-gray-500 mt-0.5">
                                          ${(rsvpGroup.ticketPrice / 100).toFixed(2)}
                                        </p>
                                      ) : null}
                                    </div>
                                  )}
                                </>
                              ) : (
                                <p className="text-sm font-medium text-gray-700">Free RSVP</p>
                              )}
                            </div>
                            
                            {/* Total Amount */}
                            <div className="text-right min-w-[100px]">
                              <p className="text-xs font-medium text-gray-500 mb-1.5">Total Amount</p>
                              {rsvpGroup.paymentAmount > 0 ? (
                                <p className="text-lg font-bold text-gray-900">
                                  ${(rsvpGroup.paymentAmount / 100).toFixed(2)}
                                </p>
                              ) : (
                                <p className="text-lg font-bold text-gray-400">
                                  $0.00
                                </p>
                              )}
                            </div>
                            
                            {/* Payment Status */}
                            <div className="text-right min-w-[120px]">
                              <p className="text-xs font-medium text-gray-500 mb-1.5">Payment Status</p>
                              {rsvpGroup.paymentDetails && rsvpGroup.paymentDetails.length > 0 ? (
                                <div className="flex flex-col items-end gap-1">
                                  {(() => {
                                    // Determine overall status: Paid (all succeeded), Pending (at least one pending), Failed (at least one failed)
                                    const hasFailed = rsvpGroup.paymentDetails.some(p => p.status === 'failed' || p.status === 'canceled');
                                    const hasPending = rsvpGroup.paymentDetails.some(p => p.status === 'pending' || p.status === 'requires_payment_method');
                                    const allSucceeded = rsvpGroup.paymentDetails.every(p => p.status === 'succeeded');
                                    
                                    let overallStatus = 'pending';
                                    if (allSucceeded) {
                                      overallStatus = 'succeeded';
                                    } else if (hasFailed) {
                                      overallStatus = 'failed';
                                    } else if (hasPending) {
                                      overallStatus = 'pending';
                                    }
                                    
                                    return renderPaymentStatusBadge(overallStatus);
                                  })()}
                                </div>
                              ) : rsvpGroup.isPaid ? (
                                <div className="flex flex-col items-end gap-1">
                                  <span className="text-sm text-gray-600">Paid</span>
                                </div>
                              ) : (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 border border-gray-200 rounded-lg text-sm font-semibold text-gray-600">
                                  Free
                                </span>
                              )}
                            </div>
                            
                            {/* Expand Icon */}
                            {rsvpGroup.hasMultipleAttendees && (
                              <div className="flex items-center">
                                <FontAwesomeIcon 
                                  icon={expandedRows.has(rsvpGroup.id) ? faChevronDown : faChevronRight} 
                                  className="w-4 h-4 text-gray-400 transition-transform duration-200"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Expanded Details */}
                      {expandedRows.has(rsvpGroup.id) && rsvpGroup.hasMultipleAttendees && (
                        <div className="border-t border-gray-200 bg-gray-50 p-5">
                          {rsvpGroup.showDetailedBreakdown ? (
                            // Detailed breakdown with ticket info - Stripe style
                            <div className="space-y-2.5">
                              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Ticket Breakdown</h4>
                              {(() => {
                                // Create array of all attendees with their payment status
                                const allAttendees = [];
                                
                                // Add primary attendee first
                                if (rsvpGroup.primaryAttendeeDetails) {
                                  const primaryPayment = rsvpGroup.paymentDetails && rsvpGroup.paymentDetails[0];
                                  allAttendees.push({
                                    isPrimary: true,
                                    name: rsvpGroup.primaryName,
                                    ticketLabel: rsvpGroup.primaryAttendeeDetails.ticketLabel || rsvpGroup.ticketTypeLabel,
                                    price: rsvpGroup.primaryAttendeeDetails.pricePaidCents !== null && rsvpGroup.primaryAttendeeDetails.pricePaidCents !== undefined
                                      ? rsvpGroup.primaryAttendeeDetails.pricePaidCents
                                      : (rsvpGroup.paymentAmount && rsvpGroup.ticketPurchaseCount === 1 
                                          ? (rsvpGroup.paymentAmount / rsvpGroup.totalAttendees) 
                                          : null),
                                    paymentStatus: primaryPayment?.status || 'pending'
                                  });
                                }
                                
                                // Add additional attendees
                                rsvpGroup.attendeeDetails.forEach((attendee, index) => {
                                  const attendeePayment = rsvpGroup.paymentDetails && rsvpGroup.paymentDetails[index + 1];
                                  const fallbackStatus = rsvpGroup.paymentDetails && rsvpGroup.paymentDetails.length === 1 ? rsvpGroup.paymentDetails[0].status : 'pending';
                                  const finalStatus = attendeePayment?.status || fallbackStatus;
                                  console.log(`Attendee ${attendee.name}: attendeePayment=`, attendeePayment, 'fallbackStatus=', fallbackStatus, 'finalStatus=', finalStatus);
                                  allAttendees.push({
                                    isPrimary: false,
                                    name: attendee.name,
                                    ticketLabel: attendee.ticketLabel,
                                    price: attendee.pricePaidCents !== null && attendee.pricePaidCents !== undefined
                                      ? attendee.pricePaidCents
                                      : (rsvpGroup.paymentAmount && rsvpGroup.ticketPurchaseCount === 1 
                                          ? (rsvpGroup.paymentAmount / rsvpGroup.totalAttendees) 
                                          : null),
                                    paymentStatus: finalStatus
                                  });
                                });
                                
                                return allAttendees.map((attendee, index) => (
                                  <div key={index} className={`flex items-center justify-between p-3 rounded-lg border ${attendee.isPrimary ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'}`}>
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                      <input 
                                        type="checkbox" 
                                        disabled 
                                        className="w-5 h-5 rounded border-gray-300 text-gray-400 cursor-default"
                                      />
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-0.5">
                                          {attendee.isPrimary && (
                                            <span className="inline-flex items-center px-1.5 py-0.5 bg-blue-600 text-white rounded text-xs font-bold uppercase tracking-wide">
                                              Primary
                                            </span>
                                          )}
                                          <span className="text-sm font-semibold text-gray-900 truncate">
                                            {attendee.name}
                                          </span>
                                        </div>
                                        <p className="text-xs text-gray-500">
                                          {attendee.ticketLabel}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-4 flex-shrink-0 ml-4">
                                      <span className="text-sm font-semibold text-gray-900">
                                        {attendee.price !== null ? `$${(attendee.price / 100).toFixed(2)}` : '$0.00'}
                                      </span>
                                      <span className="text-xs text-gray-500">USD</span>
                                      {(() => {
                                        // Don't show payment status for free tickets ($0.00)
                                        const ticketPrice = attendee.price || 0;
                                        if (ticketPrice === 0) {
                                          return (
                                            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100 border border-gray-200 rounded-md text-xs font-semibold text-gray-600 min-w-[100px] justify-center">
                                              Free
                                            </span>
                                          );
                                        }
                                        
                                        console.log(`Payment status for ${attendee.name}:`, attendee.paymentStatus, `(type: ${typeof attendee.paymentStatus})`);
                                        switch(attendee.paymentStatus) {
                                          case 'succeeded':
                                            return (
                                              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 border border-emerald-200 rounded-md text-xs font-semibold text-emerald-700 min-w-[100px] justify-center">
                                                Succeeded ✓
                                              </span>
                                            );
                                          case 'pending':
                                          case 'requires_payment_method':
                                            return (
                                              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-50 border border-amber-200 rounded-md text-xs font-semibold text-amber-700 min-w-[100px] justify-center">
                                                Pending
                                              </span>
                                            );
                                          case 'failed':
                                          case 'canceled':
                                            return (
                                              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-red-50 border border-red-200 rounded-md text-xs font-semibold text-red-700 min-w-[100px] justify-center">
                                                Failed
                                              </span>
                                            );
                                          default:
                                            return (
                                              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-50 border border-gray-200 rounded-md text-xs font-semibold text-gray-600 min-w-[100px] justify-center">
                                                Unknown
                                              </span>
                                            );
                                        }
                                      })()}
                                    </div>
                                  </div>
                                ));
                              })()}
                            </div>
                          ) : (
                            // Simple guest list
                            <div>
                              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Additional Guests</h4>
                              <div className="flex flex-wrap gap-2">
                                {rsvpGroup.attendeeDetails.map((attendee, index) => (
                                  <span key={index} className="inline-flex items-center px-3 py-1.5 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-sm font-medium">
                                    {attendee.name}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <FontAwesomeIcon icon={faUser} className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-600 font-medium">No RSVPs yet</p>
                  <p className="text-sm text-gray-500 mt-1">Attendees will appear here once they RSVP</p>
                </div>
              )}
              
              {/* Pagination */}
              {rsvpGroups.length > 0 && totalPages > 1 && (
                <div className="flex items-center justify-center gap-4 mt-6 pt-6 border-t border-gray-200">
                  <button 
                    onClick={goToPrevPage} 
                    disabled={currentPage === 1}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                      currentPage === 1 
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 shadow-sm'
                    }`}
                  >
                    ← Previous
                  </button>
                  <span className="text-sm font-medium text-gray-600">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button 
                    onClick={goToNextPage} 
                    disabled={currentPage === totalPages}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                      currentPage === totalPages 
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 shadow-sm'
                    }`}
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AttendeeModal;